// src/hooks/useContactMessages.ts
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ContactMessageService } from '../admin/pages/messages/contactMessageService';

export const MESSAGE_KEYS = {
  all: ['contactMessages'] as const,
  lists: () => ['contactMessages', 'list'] as const,
  paginated: (page: number, search: string, status: string) =>
    ['contactMessages', 'list', page, search, status] as const,
  stats: () => ['contactMessages', 'stats'] as const,
};

// FIX: Removed the `cursors` field — seek-based pagination only needs `page`.
export interface MessagePaginationParams {
  page: number;
  search: string;
  status: string;
  pageSize: number;
  // `cursors` intentionally removed — seek-based pagination uses only `page`
}

// FIX: Passes `params.page` directly to getMessagesPaginated instead of
// cursor objects. Any page can now be fetched in any order.
export const useMessagesPaginated = (params: MessagePaginationParams) => {
  return useQuery({
    queryKey: MESSAGE_KEYS.paginated(params.page, params.search, params.status),
    queryFn: () =>
      ContactMessageService.getMessagesPaginated(
        params.pageSize,
        params.page,
        params.status !== 'all' ? (params.status as 'read' | 'unread') : undefined,
        params.search
      ),
    staleTime: 1000 * 60 * 2,           // 2 min — messages change frequently (read/unread)
    placeholderData: (prev: any) => prev,
  });
};

export const useMessageStats = () => {
  return useQuery({
    queryKey: MESSAGE_KEYS.stats(),
    queryFn: () => ContactMessageService.getMessageCount(),
    staleTime: 1000 * 60 * 3,
  });
};

export const useMessageMutations = () => {
  const queryClient = useQueryClient();

  const invalidateAll = () => {
    queryClient.invalidateQueries({ queryKey: MESSAGE_KEYS.all });
  };

  const updateStatus = useMutation({
    mutationFn: ({ messageId, status }: { messageId: string; status: 'read' | 'unread' }) =>
      ContactMessageService.updateMessageStatus(messageId, status),
    onSuccess: invalidateAll,
  });

  const markAsRead = useMutation({
    mutationFn: (messageId: string) => ContactMessageService.markAsRead(messageId),
    onSuccess: invalidateAll,
  });

  const remove = useMutation({
    mutationFn: (messageId: string) => ContactMessageService.deleteMessage(messageId),
    onSuccess: invalidateAll,
  });

  return { updateStatus, markAsRead, remove };
};